import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';
import CardContent from '@dashboard/components/CardContent';

import { apiUrl } from '@dashboard/services/env';

import Statistic from './components/Statistic';
import useSWR from 'swr';
import request from '@dashboard/services/request';
import Card from '@dashboard/components/Card';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import { PAGE_TYPES } from '@dashboard/enums/page-types';

const Visits = ({ page }) => {
  const { token, sellerId } = useAuth();

  const { data, error } = useSWR(`/sellers/${sellerId}/pages/${page._id}/statistics/visits`, url =>
    request(apiUrl + url, token),
  );

  const visits = !error &&
    data?.data && {
      labels: data.data.map(day => format(new Date(day.date), 'd MMM')),
      datasets: [
        {
          chartType: 'line',
          values: data.data.map(day => day.count),
        },
      ],
    };

  return (
    <Statistic
      data={visits}
      total={data?.total}
      isLoading={!error && !data}
      error={error?.message}
      title="Visits"
    />
  );
};

Visits.propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }).isRequired,
};

const Total = ({ page }) => {
  const { token, sellerId } = useAuth();

  const type = page.type === 'form' ? 'submission' : page.product.type;

  const key = `/sellers/${sellerId}/pages/${page._id}/statistics/${type}total`;

  const { data, error } = useSWR(key, url => request(apiUrl + url, token));

  const dataset = !error &&
    data?.data && {
      labels: data.data.map(day => format(new Date(day.date), 'd MMM')),
      datasets: [
        {
          chartType: 'line',
          values: data.data.map(day => day.total),
        },
      ],
    };

  const currency = page?.product?.currency;

  return (
    <Statistic
      data={dataset}
      total={getPriceLabel({
        amount: data?.total,
        currency: currency,
      })}
      isLoading={!error && !data}
      error={error?.message}
      tooltipOptions={{
        formatTooltipY: d =>
          getPriceLabel({
            amount: d,
            currency: currency,
          }),
      }}
      title={type === 'subscription' ? 'Subscription volume' : 'Sales volume'}
    />
  );
};

Total.propTypes = {
  page: PropTypes.shape({
    type: PropTypes.oneOf(['subscription', 'charge']),
    _id: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
};

const Count = ({ page }) => {
  const { token, sellerId } = useAuth();

  let type = 'charge';
  if (page.type === 'form') {
    type = 'submission';
  } else if (page.type === PAGE_TYPES.CHECKOUT && page.product.type === 'subscription') {
    type = 'subscription';
  }

  const key = `/sellers/${sellerId}/pages/${page._id}/statistics/${type}count`;

  const { data, error } = useSWR(key, url => request(apiUrl + url, token));

  const dataset = !error &&
    data?.data && {
      labels: data.data.map(day => format(new Date(day.date), 'd MMM')),
      datasets: [
        {
          chartType: 'line',
          values: data.data.map(day => day.count),
        },
      ],
    };

  const label =
    type === 'submission'
      ? 'Number of submissions'
      : type === 'subscription'
      ? 'Number of subscriptions'
      : 'Number of sales';

  return (
    <Statistic
      data={dataset}
      total={data?.total}
      isLoading={!error && !data}
      error={error?.message}
      title={label}
    />
  );
};

Count.propTypes = {
  page: PropTypes.shape({
    type: PropTypes.oneOf(['subscription', 'charge']),
    _id: PropTypes.string.isRequired,
  }).isRequired,
};

const Referrals = ({ page }) => {
  const { token, sellerId } = useAuth();

  const [showMore, setShowMore] = useState(false);

  const key = `/sellers/${sellerId}/pages/${page._id}/statistics/referrals`;

  const { data, error } = useSWR(key, url => request(apiUrl + url, token));

  return (
    <div style={{ display: 'flex', minHeight: '150px', padding: '3em 0' }}>
      <div style={{ width: '25%', minWidth: '100px' }}>
        <div style={{ color: 'var(--gray-darker)', fontSize: '1rem' }}>Referrals</div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {(() => {
          if (!error && !data) {
            return <Spinner style={{ margin: 'auto' }} />;
          }

          if (!data?.data || error) {
            return (
              <div
                style={{
                  margin: 'auto',
                  textAlign: 'center',
                  color: 'var(--gray-darker)',
                }}
              >
                {error?.message}
              </div>
            );
          }

          if (!data?.data?.length) {
            return (
              <div
                style={{
                  margin: 'auto',
                  textAlign: 'center',
                  color: 'var(--gray-darker)',
                }}
              >
                No referrals to show
              </div>
            );
          }

          const referralsToShow = showMore ? data?.data : data?.data?.slice(0, 10);

          return (
            <div
              style={{
                marginLeft: '6.75%',
                wordBreak: 'break-word',
              }}
            >
              {referralsToShow.map(ref => (
                <div
                  style={{
                    display: 'flex',
                    marginBottom: '0.25em',
                  }}
                  key={ref.referrer}
                >
                  <div
                    style={{
                      minWidth: '30px',
                      textAlign: 'left',
                      marginRight: '0.5em',
                      color: 'var(--gray-darker)',
                      fontWeight: 'bold',
                    }}
                  >
                    {ref.count}
                  </div>
                  <div style={{ color: 'var(--gray-darker)' }}>
                    <Link to={ref.referrer}>{ref.referrer}</Link>
                  </div>
                </div>
              ))}

              {data?.data?.length > 10 ? (
                <Button handleClick={() => setShowMore(!showMore)} style={{ marginTop: '0.25em' }}>
                  {showMore ? 'Less' : 'More'}
                </Button>
              ) : (
                ''
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

Referrals.propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }).isRequired,
};

const PageAnalytics = ({ page }) => {
  return (
    <div className="border border-gray-100 rounded bg-white">
      <Card className="max-w-screen-md mx-auto mb-16">
        <h2 className="text-xl font-medium">Analytics</h2>

        <CardContent>
          <Count page={page} />
          {page.type === 'checkout' && <Total page={page} />}
          <Visits page={page} />
          <Referrals page={page} />

          {page?.product?.type === 'subscription' ? (
            <div className="mb-4 text-gray-700">
              These statistics only show new subscriptions. To view your subscription revenue and
              charges, please refer to your{' '}
              <Link to={getStripeDashboardUrl('/dashboard')} appearance="default" blank>
                Stripe Dashboard
              </Link>
              .
            </div>
          ) : (
            ''
          )}
        </CardContent>
      </Card>
    </div>
  );
};

PageAnalytics.propTypes = {
  page: PropTypes.shape({
    type: PropTypes.oneOf(['subscription', 'charge']).isRequired,
  }).isRequired,
};

export default PageAnalytics;
